/**
 * Plot size display helpers.
 *
 * The DB stores land_size in hectares (normalised from the farmer's
 * typed input). It also stores `land_size_original` + `land_size_unit`
 * — the farmer's typed number before the hectares conversion. Showing
 * the original number back to the farmer avoids rendering
 * `0.0012000000000000001 hectares` for someone who typed `12 m²`.
 *
 * Legacy rows pre-PR 10 only have `land_size` (hectares); the helpers
 * fall back to that cleanly.
 */

// Conversion factors used by the Add Plot form. Kept here so the
// formatter can convert back from hectares when a legacy row is
// missing the farmer's typed value.
export const LAND_SIZE_UNITS = [
  { value: 'hectares', toHectares: 1 },
  { value: 'acres', toHectares: 0.404686 },
  { value: 'sqMeters', toHectares: 0.0001 },
  { value: 'sqFeet', toHectares: 0.0000092903 },
];


function _formatNumber(value, locale) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  // Max 2 decimals, no superfluous trailing zeros. Intl handles the
  // locale-correct decimal separator (comma vs. dot).
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(value);
}


/**
 * Render the plot area in the farmer's own unit.
 *
 * Returns an object the UI can lay out however it wants:
 *   {
 *     primary:   "12 m²",
 *     secondary: "0.0012 ha"  // only when the unit isn't already ha
 *   }
 *
 * `primary` is always a string; `secondary` is null when the primary
 * is already in hectares.
 *
 * `t` is the i18next translator — unit labels live under
 * `farm.form.units.*`.
 */
export function formatPlotSize(plot, t, locale) {
  if (!plot) return { primary: '', secondary: null };

  const unit = plot.land_size_unit || 'hectares';
  const original = typeof plot.land_size_original === 'number'
    ? plot.land_size_original
    : plot.land_size;  // legacy rows: original wasn't stored; the "typed" number IS the hectares value

  const primary = `${_formatNumber(original, locale)} ${t(`farm.form.units.${unit}`, unit)}`;

  if (unit === 'hectares') {
    return { primary, secondary: null };
  }

  // Show hectares as a secondary line so agronomic calculations
  // (fertilisation rates, spray dose per hectare) stay legible.
  const hectares = typeof plot.land_size === 'number' ? plot.land_size : null;
  const secondary = hectares != null
    ? `${_formatNumber(hectares, locale)} ${t('farm.form.units.hectares', 'ha')}`
    : null;

  return { primary, secondary };
}


/** Convert a farmer-typed number + unit to hectares. */
export function convertToHectares(value, unit) {
  const factor = LAND_SIZE_UNITS.find((u) => u.value === unit)?.toHectares ?? 1;
  return Number.parseFloat(value) * factor;
}
